// The awards engine (AWARDS.md): facts and a catalog in, winners, lines,
// progress, and a trophy room out. No clocks, no I/O, no sport names. The
// host decides when a period is final and when to mint; this file only
// decides who won what and how to say it.

import {
  TEAM_PERIOD_METRICS,
  TEAM_SEASON_METRICS,
  milestoneHistoryValue,
  milestoneSeasonValue,
  metricCombines,
  gamesOf,
  round1,
  teamGames,
} from './metrics';
import { DEFAULT_CATALOG } from './catalog';
import {
  DEFAULT_RULES,
  TIERS,
  type MilestoneDefinition,
  type MilestoneProgress,
  type AwardDefinition,
  type AwardResult,
  type AwardRules,
  type Catalog,
  type Minted,
  type SeasonFacts,
  type TeamPeriodFact,
  type Tier,
  type TrophyRoomData,
  type Unit,
  type Winner,
} from './types';

// ── Small helpers ─────────────────────────────────────────────────────────

/** FNV-1a, so line variants are stable per league, period, and award. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export function formatValue(value: number, unit: Unit): string {
  switch (unit) {
    case 'points':
      return value.toFixed(1);
    case 'margin':
      return value.toFixed(1);
    case 'percent':
      return `${Math.round(value)}%`;
    case 'count':
      return String(Math.round(value));
    case 'none':
      return '';
  }
}

/** Fill {placeholders} from the detail; unknown ones become empty. */
export function renderLine(template: string, detail: Record<string, string | number>): string {
  return template
    .replace(/\{(\w+)\}/g, (_, key: string) => {
      const v = detail[key];
      return v === undefined ? '' : String(v);
    })
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** The league's name for an award, or the catalog's. */
export function awardName(def: { id: string; name: string }, rules: AwardRules): string {
  return rules.names[def.id] ?? def.name;
}

function teamName(facts: SeasonFacts, teamId: string): string {
  return facts.teams.find((t) => t.teamId === teamId)?.name ?? 'a team';
}

function ownerName(facts: SeasonFacts, teamId: string): string {
  return facts.teams.find((t) => t.teamId === teamId)?.ownerName ?? 'somebody';
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'] as const;
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

// ── Picking winners ───────────────────────────────────────────────────────

interface Candidate {
  teamId: string;
  value: number;
  detail: Record<string, string | number>;
}

function best(
  candidates: Candidate[],
  direction: 'max' | 'min',
): { winners: Candidate[]; standings: Candidate[] } {
  if (!candidates.length) return { winners: [], standings: [] };
  const sorted = [...candidates].sort((a, b) =>
    direction === 'max' ? b.value - a.value : a.value - b.value,
  );
  const top = sorted[0]!.value;
  return {
    winners: sorted.filter((c) => Math.abs(c.value - top) < 0.05),
    standings: sorted.slice(0, 5),
  };
}

function baseDetail(
  facts: SeasonFacts,
  teamId: string,
  opponentTeamId: string | null,
): Record<string, string | number> {
  return {
    team: teamName(facts, teamId),
    owner: ownerName(facts, teamId),
    opponent: opponentTeamId ? teamName(facts, opponentTeamId) : '',
    opponentOwner: opponentTeamId ? ownerName(facts, opponentTeamId) : '',
    season: facts.season,
    trophy: facts.trophyName,
  };
}

/**
 * How many candidates an award needs before it means anything: "the
 * closest game" of one game is not a thing, and a two-team final's loser
 * did not earn the Toilet Bowl. The championship week's honor is the title.
 */
function minimumField(def: AwardDefinition): number {
  const pick = def.pick;
  switch (pick.over) {
    case 'team-period':
      return pick.metric === 'pointsInWin' || pick.metric === 'pointsInLoss' ? 2 : 3;
    case 'matchup':
      return 2;
    default:
      return 1;
  }
}

/**
 * The metrics a week of two games (Q6) earns only as a whole: points in a
 * loss with no win beside it, points in a win with no loss.
 */
const WHOLE_WEEK: Partial<Record<string, 'win' | 'loss'>> = {
  pointsInLoss: 'loss',
  pointsInWin: 'win',
};

function candidatesFor(
  facts: SeasonFacts,
  def: AwardDefinition,
  period: number | null,
): { candidates: Candidate[]; direction: 'max' | 'min' } | null {
  const pick = def.pick;
  switch (pick.over) {
    case 'team-period': {
      const p = facts.periods.find((x) => x.period === period);
      if (!p) return null;
      const metric = TEAM_PERIOD_METRICS[pick.metric];
      const candidates: Candidate[] = [];
      // EACH TEAM ONCE, BY ITS GAME THAT MEASURES BEST (Q6): a margin or a
      // score in a win is a game's, and a week of two has two. Points in a
      // loss are a week with no win, and points in a win a week with no loss,
      // so the award's words (*no win*) are true of the whole week.
      const whole = WHOLE_WEEK[pick.metric];
      for (const week of p.teams) {
        if (whole !== undefined && !gamesOf(week).every((g) => g.result === whole)) continue;
        const better = (a: number, b: number) => (pick.best === 'max' ? a > b : a < b);
        let t: TeamPeriodFact | null = null;
        let value: number | null = null;
        for (const g of gamesOf(week)) {
          const v = metric(g);
          if (v === null || (pick.min !== undefined && v < pick.min)) continue;
          if (value === null || better(v, value)) {
            t = g;
            value = v;
          }
        }
        if (t === null || value === null) continue;
        candidates.push({
          teamId: t.teamId,
          value,
          detail: {
            ...baseDetail(facts, t.teamId, t.opponentTeamId),
            value: formatValue(value, def.unit),
            margin:
              t.opponentScore !== null
                ? formatValue(Math.abs(t.score - t.opponentScore), 'margin')
                : '',
            projected: formatValue(t.projected ?? 0, 'points'),
            actual: formatValue(t.score, 'points'),
            player: t.topStarter ? facts.playerName(t.topStarter.playerId) : '',
            share:
              t.topStarter && t.score > 0 ? Math.round((t.topStarter.points / t.score) * 100) : 0,
            period: t.period,
          },
        });
      }
      return { candidates, direction: pick.best };
    }
    case 'matchup': {
      const p = facts.periods.find((x) => x.period === period);
      if (!p) return null;
      const candidates: Candidate[] = [];
      // A TEAM ONCE, by its game that measures best (Q6): a week of two
      // games can give one winner two.
      const better = (a: number, b: number) => (pick.best === 'max' ? a > b : a < b);
      for (const m of p.matchups) {
        if (!m.final || m.homeScore === m.awayScore) continue;
        const homeWon = m.homeScore > m.awayScore;
        const winner = homeWon ? m.homeTeamId : m.awayTeamId;
        const loser = homeWon ? m.awayTeamId : m.homeTeamId;
        const margin = round1(Math.abs(m.homeScore - m.awayScore));
        const already = candidates.findIndex((c) => c.teamId === winner);
        if (already !== -1) {
          if (!better(margin, candidates[already]!.value)) continue;
          candidates.splice(already, 1);
        }
        candidates.push({
          teamId: winner,
          value: margin,
          detail: {
            ...baseDetail(facts, winner, loser),
            value: formatValue(margin, 'margin'),
            margin: formatValue(margin, 'margin'),
            period: m.period,
          },
        });
      }
      return { candidates, direction: pick.best };
    }
    case 'team-season': {
      const metric = TEAM_SEASON_METRICS[pick.metric];
      const candidates: Candidate[] = [];
      for (const t of facts.teams) {
        const r = metric(facts, t.teamId);
        if (!r) continue;
        if (pick.min !== undefined && r.value < pick.min) continue;
        candidates.push({
          teamId: t.teamId,
          value: r.value,
          detail: {
            ...baseDetail(facts, t.teamId, null),
            ...r.detail,
            value: formatValue(r.value, def.unit),
          },
        });
      }
      return { candidates, direction: pick.best };
    }
    case 'transaction': {
      const p = facts.periods.find((x) => x.period === period);
      if (!p) return null;
      const candidates: Candidate[] = [];
      for (const tx of p.transactions) {
        if (tx.kind === 'trade' || tx.pointsThisPeriod <= 0) continue;
        candidates.push({
          teamId: tx.teamId,
          value: tx.pointsThisPeriod,
          detail: {
            ...baseDetail(facts, tx.teamId, null),
            value: formatValue(tx.pointsThisPeriod, 'points'),
            player: facts.playerName(tx.playerId),
            period: tx.period,
          },
        });
      }
      return { candidates, direction: pick.best };
    }
    case 'bracket': {
      const b = facts.bracket;
      const teamId =
        pick.slot === 'champion'
          ? b.championTeamId
          : pick.slot === 'runner-up'
            ? b.runnerUpTeamId
            : b.lastPlaceTeamId;
      if (!teamId) return { candidates: [], direction: 'max' };
      const opponent =
        pick.slot === 'champion'
          ? b.runnerUpTeamId
          : pick.slot === 'runner-up'
            ? b.championTeamId
            : null;
      return {
        candidates: [
          {
            teamId,
            value: 1,
            detail: {
              ...baseDetail(facts, teamId, opponent),
              value: '',
              finalLine: b.finalLine ?? '',
            },
          },
        ],
        direction: 'max',
      };
    }
  }
}

function evaluate(
  facts: SeasonFacts,
  def: AwardDefinition,
  period: number | null,
  rules: AwardRules,
  asItStands: boolean,
): AwardResult | null {
  const picked = candidatesFor(facts, def, period);
  if (!picked || picked.candidates.length < minimumField(def)) return null;
  const { winners, standings } = best(picked.candidates, picked.direction);
  if (!winners.length) return null;
  const key = `${facts.leagueId}:${facts.season}:${period ?? 'season'}:${def.id}`;
  const template = def.lines[hashString(key) % def.lines.length] ?? def.lines[0] ?? '';
  const first = winners[0]!;
  const detail =
    winners.length > 1
      ? {
          ...first.detail,
          team: winners.map((w) => w.detail.team).join(' and '),
          owner: winners.map((w) => w.detail.owner).join(' and '),
        }
      : first.detail;
  return {
    awardId: def.id,
    scope: def.scope,
    period,
    name: awardName(def, rules),
    blurb: def.blurb,
    icon: def.icon,
    tone: def.tone,
    unit: def.unit,
    asItStands,
    winners: winners.map<Winner>((w) => ({ teamId: w.teamId, value: w.value, detail: w.detail })),
    standings: standings.map((c) => ({ teamId: c.teamId, value: c.value })),
    line: renderLine(template, detail),
  };
}

function enabled(def: { id: string }, rules: AwardRules): boolean {
  return !rules.disabled.includes(def.id);
}

/**
 * The period's weekly awards: minted when the host says the period is
 * final, "as it stands" before. A matchup-based award needs decided
 * matchups; nothing is returned for an award nobody qualifies for.
 */
export function periodAwards(
  facts: SeasonFacts,
  period: number,
  catalog: Catalog = DEFAULT_CATALOG,
  rules: AwardRules = DEFAULT_RULES,
): AwardResult[] {
  if (!rules.perPeriod) return [];
  const p = facts.periods.find((x) => x.period === period);
  if (!p) return [];
  const out: AwardResult[] = [];
  for (const def of catalog.awards) {
    if (def.scope !== 'period' || !enabled(def, rules)) continue;
    const r = evaluate(facts, def, period, rules, !p.final);
    if (r) out.push(r);
  }
  return out;
}

/** The season's awards: final once the bracket is, the race before. */
export function seasonAwards(
  facts: SeasonFacts,
  catalog: Catalog = DEFAULT_CATALOG,
  rules: AwardRules = DEFAULT_RULES,
): AwardResult[] {
  if (!rules.perSeason) return [];
  const out: AwardResult[] = [];
  for (const def of catalog.awards) {
    if (def.scope !== 'season' || !enabled(def, rules)) continue;
    const r = evaluate(facts, def, null, rules, !facts.bracket.final);
    if (r) out.push(r);
  }
  return out;
}

/** A minted record for a result the host has decided to hand out. */
export function mint(facts: SeasonFacts, result: AwardResult, at: number): Minted {
  return {
    ...result,
    asItStands: false,
    id: `${facts.leagueId}:${facts.season}:${result.period ?? 'season'}:${result.awardId}`,
    at,
    supersededBy: null,
  };
}

// ── Milestones ────────────────────────────────────────────────────────────

function plural(n: number, word: string): string {
  return `${Math.round(n)} ${word}${Math.round(n) === 1 ? '' : 's'}`;
}

/** What a milestone line may name: the value, the owner, and the counted things in the right number. */
function lineDetail(value: number, unit: Unit, owner: string): Record<string, string | number> {
  return {
    value: formatValue(value, unit),
    owner,
    times: Math.round(value) === 1 ? 'once' : `${Math.round(value)} times`,
    games: plural(value, 'game'),
    claims: plural(value, 'waiver claim'),
    trades: plural(value, 'trade'),
    trips: plural(value, 'playoff trip'),
    titles: plural(value, 'championship'),
    awards: plural(value, 'weekly award'),
    bowls: plural(value, 'Toilet Bowl'),
    weeks: plural(value, 'week'),
  };
}

function progressFor(
  def: MilestoneDefinition,
  value: number,
  rules: AwardRules,
  owner: string,
): MilestoneProgress {
  const earned = def.tiers.filter((t) => value >= t.threshold);
  const top = earned[earned.length - 1] ?? null;
  const next = def.tiers.find((t) => value < t.threshold) ?? null;
  const prevThreshold = top ? top.threshold : 0;
  const progress = next
    ? Math.max(0, Math.min(1, (value - prevThreshold) / (next.threshold - prevThreshold)))
    : 1;
  return {
    id: def.id,
    name: awardName(def, rules),
    blurb: def.blurb,
    icon: def.icon,
    tone: def.tone,
    scope: def.scope,
    unit: def.unit,
    value,
    tier: top?.tier ?? null,
    earnedTiers: earned.map((t) => t.tier),
    nextTier: next?.tier ?? null,
    nextThreshold: next?.threshold ?? null,
    progress,
    earnedLine: top ? renderLine(def.line, lineDetail(value, def.unit, owner)) : null,
    tiers: def.tiers.map((t) => ({ ...t, earned: value >= t.threshold })),
  };
}

/** Every milestone's progress for one team, career ones summed with the league's history. */
export function milestoneProgress(
  facts: SeasonFacts,
  teamId: string,
  minted: readonly Minted[],
  catalog: Catalog = DEFAULT_CATALOG,
  rules: AwardRules = DEFAULT_RULES,
): MilestoneProgress[] {
  if (!rules.milestones) return [];
  const owner = ownerName(facts, teamId);
  const out: MilestoneProgress[] = [];
  for (const def of catalog.milestones) {
    if (!enabled(def, rules)) continue;
    let value = milestoneSeasonValue(facts, teamId, def.metric, minted);
    if (def.scope === 'career') {
      const combine = metricCombines(def.metric);
      for (const season of facts.history) {
        const past = season.teams.find((t) => t.teamId === teamId);
        if (!past) continue;
        const v = milestoneHistoryValue(past, def.metric);
        value = combine === 'sum' ? value + v : Math.max(value, v);
      }
    }
    out.push(progressFor(def, value, rules, owner));
  }
  return out;
}

/**
 * The season's facts as they stood when `period` was settled, and the weekly
 * awards minted by then: what a host compares a week's milestones against to
 * say which week earned a tier (PLAN-2026-10-29-milestones-as-they-happen §1).
 * The weeks after it go, with their claims and trades; the current week is the
 * one after it at most, which is what lets a playoff trip count once the
 * regular season's last week has settled; and a champion whose final had not
 * been played by then is not one yet. `period` 0 is the season before any
 * week, where only the seasons before it count.
 */
export function factsThrough(
  facts: SeasonFacts,
  minted: readonly Minted[],
  period: number,
): { facts: SeasonFacts; minted: Minted[] } {
  const finalWeeks = facts.bracket.finalPeriods?.length
    ? facts.bracket.finalPeriods
    : facts.periods.filter((p) => p.kind === 'playoff').map((p) => p.period);
  // A league with no bracket (points-total, elimination) crowns the table's
  // leader when the regular season's last week is played.
  const decided =
    finalWeeks.length > 0 ? Math.max(...finalWeeks) <= period : period >= facts.regularSeasonEnd;
  return {
    facts: {
      ...facts,
      currentPeriod: Math.min(facts.currentPeriod, period + 1),
      periods: facts.periods.filter((p) => p.period <= period),
      bracket: decided
        ? facts.bracket
        : {
            ...facts.bracket,
            final: false,
            championTeamId: null,
            runnerUpTeamId: null,
            finalLine: null,
          },
    },
    minted: minted.filter((m) => m.scope === 'period' && m.period !== null && m.period <= period),
  };
}

/**
 * When a recorded milestone tier was earned, as a person reads it: the week's
 * label, or *Before this season* for a career tier the seasons before reached
 * (recorded with no week).
 */
export function earnedInLabel(period: number | null, label: string | null): string {
  return period === null ? 'Before this season' : (label ?? `Week ${period}`);
}

/** Tiers earned now that were not before; the host posts these. */
export function newUnlocks(
  before: readonly MilestoneProgress[],
  after: readonly MilestoneProgress[],
): { progress: MilestoneProgress; tier: Tier }[] {
  const out: { progress: MilestoneProgress; tier: Tier }[] = [];
  for (const a of after) {
    const b = before.find((x) => x.id === a.id);
    for (const tier of a.earnedTiers)
      if (!b || !b.earnedTiers.includes(tier)) out.push({ progress: a, tier });
  }
  return out;
}

export function tierRank(tier: Tier): number {
  return TIERS.indexOf(tier);
}

// ── The trophy room ───────────────────────────────────────────────────────

/**
 * One manager's room: the career line, a shelf per season with trophies
 * and medals, and every milestone with progress. Minted awards are the
 * source for this season; history for the rest.
 */
export function trophyRoom(
  facts: SeasonFacts,
  teamId: string,
  minted: readonly Minted[],
  catalog: Catalog = DEFAULT_CATALOG,
  rules: AwardRules = DEFAULT_RULES,
): TrophyRoomData {
  const defs = new Map(catalog.awards.map((d) => [d.id, d]));
  const live = minted.filter((m) => !m.supersededBy && m.winners.some((w) => w.teamId === teamId));
  const weeklyThisSeason = new Map<string, number>();
  for (const m of live)
    if (m.scope === 'period')
      weeklyThisSeason.set(m.awardId, (weeklyThisSeason.get(m.awardId) ?? 0) + 1);
  const careerWeekly = new Map(weeklyThisSeason);
  for (const season of facts.history) {
    const past = season.teams.find((t) => t.teamId === teamId);
    if (!past) continue;
    for (const [id, n] of Object.entries(past.weeklyAwards))
      careerWeekly.set(id, (careerWeekly.get(id) ?? 0) + n);
  }
  const medalsFrom = (counts: Map<string, number>) =>
    [...counts.entries()]
      .map(([awardId, count]) => {
        const def = defs.get(awardId);
        return def
          ? {
              awardId,
              name: awardName(def, rules),
              icon: def.icon,
              tone: def.tone,
              count,
              careerCount: careerWeekly.get(awardId) ?? count,
            }
          : null;
      })
      .filter((m): m is NonNullable<typeof m> => m !== null)
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const standing = facts.standings.find((s) => s.teamId === teamId);
  const rows = teamGames(facts, teamId);
  const wins = rows.filter((t) => t.result === 'win').length;
  const losses = rows.filter((t) => t.result === 'loss').length;
  const ties = rows.filter((t) => t.result === 'tie').length;
  const bracket = facts.bracket;
  const isChampion = bracket.final && bracket.championTeamId === teamId;
  const isRunnerUp = bracket.final && bracket.runnerUpTeamId === teamId;
  const placed = standing ? `${ordinal(standing.rank)} of ${facts.standings.length}` : '';
  const finish = isChampion
    ? 'Champion'
    : isRunnerUp
      ? 'Runner-up'
      : bracket.final
        ? placed
        : rows.length
          ? `In progress · ${placed}`
          : 'Not started';
  const currentSeason = {
    season: facts.season,
    isCurrent: true,
    finish,
    record: `${wins}–${losses}${ties ? `–${ties}` : ''}`,
    trophies: live
      .filter((m) => m.scope === 'season')
      .map((m) => ({
        awardId: m.awardId,
        name: m.name,
        icon: m.icon,
        tone: m.tone,
        line: m.line,
        champion: m.awardId === 'champion',
      })),
    medals: medalsFrom(weeklyThisSeason),
  };

  const pastSeasons = [...facts.history]
    .sort((a, b) => b.season - a.season)
    .map((season) => {
      const past = season.teams.find((t) => t.teamId === teamId);
      if (!past) return null;
      const counts = new Map(Object.entries(past.weeklyAwards));
      return {
        season: season.season,
        isCurrent: false,
        finish: past.champion
          ? 'Champion'
          : past.runnerUp
            ? 'Runner-up'
            : `${ordinal(past.finish)} of ${past.of}`,
        record: `${past.wins}–${past.losses}${past.ties ? `–${past.ties}` : ''}`,
        trophies: past.seasonAwards
          .map((awardId) => {
            const def = defs.get(awardId);
            if (!def) return null;
            const name = awardId === 'champion' ? facts.trophyName : awardName(def, rules);
            return {
              awardId,
              name,
              icon: def.icon,
              tone: def.tone,
              line:
                awardId === 'champion'
                  ? `${season.season} champion.`
                  : awardId === 'runner-up'
                    ? `${season.season} runner-up.`
                    : `${name}, ${season.season}.`,
              champion: awardId === 'champion',
            };
          })
          .filter((t): t is NonNullable<typeof t> => t !== null),
        medals: medalsFrom(counts),
      };
    })
    .filter((s): s is NonNullable<typeof s> => s !== null);

  const history = facts.history
    .map((s) => s.teams.find((t) => t.teamId === teamId))
    .filter((t): t is NonNullable<typeof t> => t !== undefined);
  const bestThisSeason = rows.reduce<{ value: number; period: number } | null>(
    (b, t) => (!b || t.score > b.value ? { value: t.score, period: t.period } : b),
    null,
  );
  let bestWeek: TrophyRoomData['career']['bestWeek'] = bestThisSeason
    ? { value: bestThisSeason.value, season: facts.season, period: bestThisSeason.period }
    : null;
  for (const h of facts.history) {
    const past = h.teams.find((t) => t.teamId === teamId);
    if (past && (!bestWeek || past.bestWeek > bestWeek.value))
      bestWeek = { value: past.bestWeek, season: h.season, period: null };
  }
  const inPlayoffsNow =
    facts.bracket.playoffTeamIds.includes(teamId) && facts.currentPeriod > facts.regularSeasonEnd;

  return {
    teamId,
    career: {
      seasons: history.length + (rows.length ? 1 : 0),
      wins: wins + history.reduce((n, h) => n + h.wins, 0),
      losses: losses + history.reduce((n, h) => n + h.losses, 0),
      ties: ties + history.reduce((n, h) => n + h.ties, 0),
      pointsFor: round1(
        rows.reduce((s, t) => s + t.score, 0) + history.reduce((n, h) => n + h.pointsFor, 0),
      ),
      titles: (isChampion ? 1 : 0) + history.filter((h) => h.champion).length,
      playoffTrips: (inPlayoffsNow ? 1 : 0) + history.filter((h) => h.playoffs).length,
      bestWeek,
    },
    seasons: [currentSeason, ...pastSeasons],
    milestones: milestoneProgress(facts, teamId, minted, catalog, rules),
  };
}

/** "3 seasons · 27–14 · 1 title · 2 playoff trips · best week 171.3". */
export function careerLine(room: TrophyRoomData): string {
  const c = room.career;
  const parts = [
    `${c.seasons} season${c.seasons === 1 ? '' : 's'}`,
    `${c.wins}–${c.losses}${c.ties ? `–${c.ties}` : ''}`,
  ];
  if (c.titles) parts.push(`${c.titles} title${c.titles === 1 ? '' : 's'}`);
  if (c.playoffTrips)
    parts.push(`${c.playoffTrips} playoff trip${c.playoffTrips === 1 ? '' : 's'}`);
  if (c.bestWeek) parts.push(`best week ${c.bestWeek.value.toFixed(1)}`);
  return parts.join(' · ');
}
